import { useEffect, useState } from "react";

import type {
  QuotaForecastingEntriesResponse,
  QuotaForecastingEntry,
  QuotaForecastingEntryOrder,
  QuotaForecastingFilters,
  QuotaForecastingSummaryResponse,
  QuotaForecastingTotals,
} from "../types/quotaForecasting";

import {
  getQuotaForecastingSummaryAsUser,
  listQuotaForecastingEntriesAsUser,
} from "../dashboard-client";

export const DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface QuotaForecastingStatsState {
  filters: Required<
    Pick<QuotaForecastingFilters, "fromTimestampMs" | "toTimestampMs">
  > &
    Omit<QuotaForecastingFilters, "fromTimestampMs" | "toTimestampMs">;
  order: QuotaForecastingEntryOrder;
  page: number;
  pageSize: number;
  summary: QuotaForecastingSummaryResponse | null;
  entries: QuotaForecastingEntriesResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export interface UseQuotaForecastingStatsOptions {
  initialFilters?: QuotaForecastingFilters;
  debounceMs?: number;
  initialPageSize?: number;
  initialOrder?: QuotaForecastingEntryOrder;
}

export interface UseQuotaForecastingStatsResult extends QuotaForecastingStatsState {
  totals: QuotaForecastingTotals | null;
  entriesRows: QuotaForecastingEntry[];
  totalRows: number;
  pageCount: number;
  setFilters: (patch: QuotaForecastingFilters) => void;
  setDateRange: (fromTimestampMs: number, toTimestampMs: number) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  refresh: () => void;
  reset: () => void;
}

interface FetchQuotaForecastingSummaryOptions {
  filters?: QuotaForecastingFilters;
}

interface FetchQuotaForecastingEntriesOptions {
  filters?: QuotaForecastingFilters;
  page?: number;
  pageSize?: number;
  order?: QuotaForecastingEntryOrder;
}

interface QuotaForecastingApi {
  fetchSummary: (
    options?: FetchQuotaForecastingSummaryOptions,
  ) => Promise<QuotaForecastingSummaryResponse>;
  fetchEntries: (
    options?: FetchQuotaForecastingEntriesOptions,
  ) => Promise<QuotaForecastingEntriesResponse>;
}

function buildQuery(filters: QuotaForecastingFilters | undefined) {
  if (!filters) {
    return {};
  }

  return {
    from:
      typeof filters.fromTimestampMs === "number"
        ? String(filters.fromTimestampMs)
        : undefined,
    model: filters.model,
    provider: filters.provider,
    to:
      typeof filters.toTimestampMs === "number"
        ? String(filters.toTimestampMs)
        : undefined,
  };
}

async function fetchSummary(
  options: FetchQuotaForecastingSummaryOptions = {},
): Promise<QuotaForecastingSummaryResponse> {
  const { data } = await getQuotaForecastingSummaryAsUser({
    query: buildQuery(options.filters),
  });
  return data;
}

async function fetchEntries(
  options: FetchQuotaForecastingEntriesOptions = {},
): Promise<QuotaForecastingEntriesResponse> {
  const { data } = await listQuotaForecastingEntriesAsUser({
    query: {
      ...buildQuery(options.filters),
      order: options.order,
      page: typeof options.page === "number" ? String(options.page) : undefined,
      pageSize:
        typeof options.pageSize === "number"
          ? String(options.pageSize)
          : undefined,
    },
  });
  return data;
}

export function useQuotaForecastingStats(
  options: UseQuotaForecastingStatsOptions = {},
): UseQuotaForecastingStatsResult {
  const [controller] = useState(
    () =>
      new QuotaForecastingStatsController({
        api: { fetchEntries, fetchSummary },
        debounceMs: options.debounceMs,
        initialFilters: options.initialFilters,
        initialOrder: options.initialOrder,
        initialPageSize: options.initialPageSize,
      }),
  );
  const [state, setState] = useState<QuotaForecastingStatsState>(() =>
    controller.getSnapshot(),
  );

  useEffect(() => {
    const unsubscribe = controller.subscribe(() =>
      setState(controller.getSnapshot()),
    );
    controller.start();
    return () => {
      unsubscribe();
      controller.dispose();
    };
  }, [controller]);

  const totals = state.summary?.totals ?? null;
  const entriesRows = state.entries?.entries ?? [];
  const totalRows = state.entries?.total ?? 0;
  const pageCount = state.entries
    ? Math.max(1, Math.ceil(state.entries.total / state.entries.pageSize))
    : 1;

  return {
    ...state,
    entriesRows,
    nextPage: controller.nextPage,
    pageCount,
    prevPage: controller.prevPage,
    refresh: controller.refresh,
    reset: controller.reset,
    setDateRange: controller.setDateRange,
    setFilters: controller.setFilters,
    setPage: controller.setPage,
    setPageSize: controller.setPageSize,
    totalRows,
    totals,
  };
}

export class QuotaForecastingStatsController {
  private readonly api: QuotaForecastingApi;
  private readonly debounceMs: number;
  private readonly defaultState: QuotaForecastingStatsState;

  private state: QuotaForecastingStatsState;
  private listeners = new Set<() => void>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private requestId = 0;
  private hasStarted = false;

  constructor(options: {
    api: QuotaForecastingApi;
    initialFilters?: QuotaForecastingFilters;
    debounceMs?: number;
    initialPageSize?: number;
    initialOrder?: QuotaForecastingEntryOrder;
  }) {
    this.api = options.api;
    this.debounceMs =
      typeof options.debounceMs === "number"
        ? Math.max(0, Math.trunc(options.debounceMs))
        : 250;

    const now = Date.now();
    const mergedFilters: QuotaForecastingStatsState["filters"] = {
      fromTimestampMs: now - DEFAULT_WINDOW_MS,
      toTimestampMs: now,
      ...options.initialFilters,
    };

    this.defaultState = {
      entries: null,
      error: null,
      filters: mergedFilters,
      isLoading: false,
      order: options.initialOrder ?? "desc",
      page: 1,
      pageSize:
        typeof options.initialPageSize === "number"
          ? Math.max(1, Math.trunc(options.initialPageSize))
          : 50,
      summary: null,
    };
    this.state = { ...this.defaultState };
  }

  start() {
    if (this.hasStarted) {
      return;
    }
    this.hasStarted = true;
    this.refresh();
  }

  dispose() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): QuotaForecastingStatsState {
    return this.state;
  }

  readonly setFilters = (patch: QuotaForecastingFilters) => {
    this.state = {
      ...this.state,
      filters: { ...this.state.filters, ...patch },
      page: 1,
    };
    this.emit();
    this.scheduleRefresh();
  };

  readonly setDateRange = (fromTimestampMs: number, toTimestampMs: number) => {
    this.state = {
      ...this.state,
      filters: { ...this.state.filters, fromTimestampMs, toTimestampMs },
      page: 1,
    };
    this.emit();
    this.scheduleRefresh();
  };

  readonly setPage = (page: number) => {
    this.state = { ...this.state, page: Math.max(1, Math.trunc(page)) };
    this.emit();
    this.refresh();
  };

  readonly setPageSize = (pageSize: number) => {
    this.state = {
      ...this.state,
      page: 1,
      pageSize: Math.max(1, Math.trunc(pageSize)),
    };
    this.emit();
    this.refresh();
  };

  readonly nextPage = () => {
    this.setPage(this.state.page + 1);
  };

  readonly prevPage = () => {
    this.setPage(Math.max(1, this.state.page - 1));
  };

  readonly reset = () => {
    this.state = { ...this.defaultState };
    this.emit();
    this.refresh();
  };

  readonly refresh = () => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    const requestId = ++this.requestId;
    this.state = { ...this.state, error: null, isLoading: true };
    this.emit();

    void Promise.all([
      this.api.fetchSummary({ filters: this.state.filters }),
      this.api.fetchEntries({
        filters: this.state.filters,
        order: this.state.order,
        page: this.state.page,
        pageSize: this.state.pageSize,
      }),
    ])
      .then(([summary, entries]) => {
        if (requestId !== this.requestId) {
          return;
        }
        this.state = {
          ...this.state,
          entries,
          error: null,
          isLoading: false,
          summary,
        };
        this.emit();
      })
      .catch((error: unknown) => {
        if (requestId !== this.requestId) {
          return;
        }
        this.state = {
          ...this.state,
          error: error instanceof Error ? error : new Error("Request failed."),
          isLoading: false,
        };
        this.emit();
      });
  };

  private scheduleRefresh() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.refresh();
    }, this.debounceMs);
  }

  private emit() {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const __testing = { DEFAULT_WINDOW_MS, QuotaForecastingStatsController };
